import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'motion/react';
import { ArrowRight, CaretRight, ChatCircleDots, Check, Infinity as InfinityIcon, LockSimple, Sparkle, X } from '@phosphor-icons/react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, ApiError, idempotencyKey } from './api';
import { trackFunnel, trackCheckoutError } from './analytics';
import { readLifecycleAttribution } from './lifecycle-attribution';
import { continuationNavigationState, readPendingContinuation, rememberPendingContinuation } from './pending-continuation';
import {
  CHOOSER_TITLE, DEFAULT_QUESTION_PACK_ID, QUESTION_PACK_COPY,
  chooserSubtitle, commerceAttribution, purchaseCtaLabel, questionPackDescription, shouldTrackPackageSelected,
} from './package-chooser-model';
import { ErrorNotice, isInternalClientError } from './error-notice';
import {
  getTelegramAuthStatus,
  isTelegramAuthPending,
  isTelegramMiniApp,
  looksLikeTelegramMiniAppShell,
  openExternalUrl,
  subscribeTelegramAuthStatus,
  type TelegramAuthStatus,
} from './telegram';
import type { EntitlementBalance, Product } from './types';

function useTelegramAuthStatus(): TelegramAuthStatus {
  const [status, setStatus] = useState<TelegramAuthStatus>(() => getTelegramAuthStatus());
  useEffect(() => subscribeTelegramAuthStatus(setStatus), []);
  return status;
}

const REPORT_PRODUCT_IDS = new Set([
  'RELATIONSHIPS_V1',
  'MONEY_V1',
  'CAREER_V1',
  'PERSONAL_YEAR_V1',
  'COMPATIBILITY_V1',
]);

export function isReportProductId(id: string): boolean {
  return REPORT_PRODUCT_IDS.has(id);
}

export type { EntitlementBalance };

const RECEIPT_EMAIL_STORAGE_KEY = 'numerology.receiptEmail';
const RECEIPT_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function rememberedReceiptEmail(): string {
  try {
    return window.localStorage.getItem(RECEIPT_EMAIL_STORAGE_KEY) ?? '';
  } catch {
    return '';
  }
}

function rememberReceiptEmail(value: string): void {
  try {
    window.localStorage.setItem(RECEIPT_EMAIL_STORAGE_KEY, value);
  } catch {
    // Private mode / disabled storage: the field simply stays empty next time.
  }
}

function ReceiptContactStep({ email, setEmail, error, onContinue, onBack, busy }: {
  email: string; setEmail: (value: string) => void; error: string | null;
  onContinue: () => void; onBack: () => void; busy: boolean;
}) {
  const formRef = useRef<HTMLFormElement>(null);
  useEffect(() => {
    const form = formRef.current;
    if (!form) return;
    const viewport = window.visualViewport;
    const overlay = form.closest<HTMLElement>('.modal-backdrop');
    const sheet = form.closest<HTMLElement>('.paywall-sheet');
    const previous = { top: overlay?.style.top ?? '', bottom: overlay?.style.bottom ?? '', maxHeight: sheet?.style.maxHeight ?? '' };
    const fit = () => {
      const height = viewport?.height ?? window.innerHeight;
      const top = viewport?.offsetTop ?? 0;
      // Mobile keyboards may shrink only visualViewport, leaving 100dvh unchanged.
      if (overlay) {
        overlay.style.top = `${top}px`;
        overlay.style.bottom = `${Math.max(0, window.innerHeight - top - height)}px`;
      }
      if (sheet) sheet.style.maxHeight = `${Math.max(120, height - 32)}px`;
      form.style.maxHeight = `${Math.max(80, height - 100)}px`;
    };
    fit();
    viewport?.addEventListener('resize', fit);
    viewport?.addEventListener('scroll', fit);
    window.addEventListener('resize', fit);
    return () => {
      viewport?.removeEventListener('resize', fit);
      viewport?.removeEventListener('scroll', fit);
      window.removeEventListener('resize', fit);
      if (overlay) { overlay.style.top = previous.top; overlay.style.bottom = previous.bottom; }
      if (sheet) sheet.style.maxHeight = previous.maxHeight;
    };
  }, []);
  return <form ref={formRef} noValidate className="receipt-contact-step" onSubmit={event => { event.preventDefault(); onContinue(); }}>
    <h2>Куда отправить чек?</h2>
    <label className="field-block"><span>Email</span>
      <input autoFocus type="email" inputMode="email" autoComplete="email" value={email}
        onChange={event => setEmail(event.target.value)} placeholder="name@example.com" aria-invalid={Boolean(error)} />
      <small className={error ? 'field-error' : undefined}>{error ?? 'Нужен только для чека'}</small>
    </label>
    <button className="primary-button" type="submit" disabled={busy}>Продолжить к оплате</button>
    <button className="text-button" type="button" onClick={onBack} disabled={busy}>Назад</button>
  </form>;
}

function formatUnlimitedUntil(value: string | null): string {
  if (!value) return '';
  return new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long' }).format(new Date(value));
}

export function entitlementStatusLabel(balance: EntitlementBalance | undefined): string {
  if (!balance) return '';
  if (balance.unlimitedActive) return `Безлимит активен до ${formatUnlimitedUntil(balance.unlimitedUntil)}`;
  const total = balance.freeQuestionsRemaining + balance.paidQuestionsRemaining;
  if (total > 0) return `Осталось вопросов: ${total}`;
  return 'Нужна оплата для следующего вопроса';
}

/** Shown only for disabled / not-yet-saleable report products. */
export function ReportCheckoutStub({ onClose }: { onClose: () => void }) {
  return <div className="modal-backdrop" role="presentation" onMouseDown={event => event.target === event.currentTarget && onClose()}>
    <motion.div className="bottom-sheet" role="dialog" aria-modal="true" initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }}>
      <button className="icon-button sheet-close" onClick={onClose} aria-label="Закрыть"><X /></button>
      <div className="sheet-symbol"><LockSimple weight="fill" /></div>
      <h2>Покупка скоро откроется</h2>
      <p>Разбор уже готов по содержанию. Оплату подключим отдельно - сейчас можно посмотреть описание и выбрать другие доступные разборы.</p>
      <button className="primary-button" onClick={onClose}>Понятно</button>
    </motion.div>
  </div>;
}

const QUESTION_PACK_IDS = ['CHAT_5', 'CHAT_15', 'CHAT_UNLIMITED_1M'];
const PRIMARY_QUESTION_PACK_ID = 'CHAT_5';
const CHECKOUT_RETRY_MESSAGE = 'Оплата сейчас не открылась. Вопрос и выбранный пакет сохранены - можно попробовать ещё раз.';

export function PaywallSheet({
  onClose,
  onSelect,
  preservedQuestion,
  sourceSurface,
  angleType,
  questionRank,
  retryProductId,
}: {
  onClose: () => void;
  onSelect: (productId: string) => void;
  preservedQuestion?: string;
  sourceSurface?: 'introductory_reading' | 'daily_forecast' | 'free_question' | 'chat' | 'numbers_explainer';
  angleType?: string;
  questionRank?: 'primary' | 'secondary';
  retryProductId?: string;
}) {
  const navigate = useNavigate();
  const catalog = useQuery({ queryKey: ['catalog'], staleTime: 300_000, queryFn: () => api.get<{ products: Product[] }>('/v1/catalog') });
  const receipt = useQuery({ queryKey: ['receipt-config'], queryFn: () => api.get<{ requireCustomerEmail: boolean }>('/v1/billing/receipt-config') });
  const runtime = useQuery({
    queryKey: ['runtime'],
    queryFn: () => api.get<{ fakePaymentsEnabled: boolean; paymentProvider: string; checkoutAvailable?: boolean }>('/v1/runtime'),
  });
  const telegramAuth = useTelegramAuthStatus();
  const offers = QUESTION_PACK_IDS.flatMap(id => {
    const product = catalog.data?.products.find(item => item.productId === id && item.checkoutEnabled && item.price);
    return product ? [product] : [];
  });
  const [selectedId, setSelectedId] = useState<string>(retryProductId && QUESTION_PACK_IDS.includes(retryProductId) ? retryProductId : PRIMARY_QUESTION_PACK_ID);
  const [email, setEmail] = useState(rememberedReceiptEmail);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [receiptStep, setReceiptStep] = useState(false);
  const [checkoutError, setCheckoutError] = useState<unknown>(null);
  const [paying, setPaying] = useState(false);
  const selected = offers.find(item => item.productId === selectedId) ?? offers[0];
  const rank = questionRank ?? readPendingContinuation()?.questionRank;
  const attribution = { ...commerceAttribution({ sourceSurface, angleType, questionRank: rank }), questionContextPresent: Boolean(preservedQuestion), chooserVersion: 3 };
  const openedAt = useRef(Date.now());
  const dismiss = () => {
    void trackFunnel('package_sheet_dismissed', { source: 'paywall', ...attribution, productId: selected?.productId, elapsedMs: Date.now() - openedAt.current });
    onClose();
  };
  useEffect(() => {
    void trackFunnel('paywall_viewed', { source: 'paywall', ...attribution });
    void trackFunnel('package_chooser_opened', { source: 'paywall', ...attribution });
    void trackFunnel('package_question_context_present', { source: 'paywall', ...attribution });
    void trackFunnel('package_default_offer_impression', { source: 'paywall', ...attribution, productId: DEFAULT_QUESTION_PACK_ID });
  }, [sourceSurface, angleType, rank]);
  useEffect(() => {
    if (preservedQuestion) void api.put('/v1/billing/purchase-intent', { productId: selectedId, question: preservedQuestion, ...commerceAttribution({ sourceSurface, angleType, questionRank: rank }) }).catch(() => {});
  }, [selectedId, preservedQuestion]);
  const insideVerifiedMiniApp = looksLikeTelegramMiniAppShell()
    || isTelegramMiniApp()
    || telegramAuth === 'authenticated'
    || telegramAuth === 'authenticating';
  const emailRequired = Boolean(receipt.data?.requireCustomerEmail);
  const choosePack = (productId: string) => {
    if (!shouldTrackPackageSelected(selectedId, productId)) return;
    setSelectedId(productId);
    setCheckoutError(null);
    const chosen = commerceAttribution({ productId, sourceSurface, angleType, questionRank: rank });
    void trackFunnel('offer_selected', { source: 'paywall', ...chosen });
    void trackFunnel('package_selected', { source: 'paywall', ...chosen });
  };
  const startCheckout = async (resumed = false) => {
    if (!selected || paying) return;
    const productId = selected.productId;
    const fields = commerceAttribution({ productId, sourceSurface, angleType, questionRank: rank });
    if (!resumed) void trackFunnel('purchase_cta_clicked', { source: 'paywall', ...fields });
    const config = receipt.isSuccess ? receipt.data : (await receipt.refetch()).data;
    if (!config) { setCheckoutError(new Error('Не удалось проверить данные для чека. Попробуй ещё раз.')); return; }
    if (config.requireCustomerEmail && !RECEIPT_EMAIL_PATTERN.test(email.trim())) {
      setReceiptStep(true);
      setEmailError(resumed ? 'Проверь email и попробуй ещё раз.' : null);
      void trackFunnel(resumed ? 'receipt_contact_invalid' : 'receipt_contact_required', { productId, source: 'paywall' });
      return;
    }
    if (config.requireCustomerEmail) rememberReceiptEmail(email.trim());
    if (resumed) {
      void trackFunnel('receipt_contact_submitted', { productId, source: 'paywall' });
      void trackFunnel('checkout_resumed_after_receipt_contact', { productId, source: 'paywall' });
    }
    setEmailError(null);
    setCheckoutError(null);
    setPaying(true);
    try {
      await api.put('/v1/billing/purchase-intent', { productId, ...(preservedQuestion ? { question: preservedQuestion } : {}), ...commerceAttribution({ sourceSurface, angleType, questionRank: rank }) });
      await trackFunnel('checkout_request_started', { source: 'paywall', ...fields });
      const campaign = readLifecycleAttribution();
      const created = await api.post<{
        orderId: string;
        confirmationUrl: string | null;
        product: { productId: string; amountMinor: number };
      }>('/v1/billing/checkout', {
        productId,
        ...(preservedQuestion ? { pendingQuestion: preservedQuestion } : {}),
        ...fields,
        ...(campaign?.sourceCampaign ? { sourceCampaign: campaign.sourceCampaign } : {}),
        ...(emailRequired ? { receiptEmail: email.trim() } : {}),
      }, idempotencyKey('checkout'));
      if (emailRequired) rememberReceiptEmail(email.trim());
      if (created.confirmationUrl?.startsWith('/')) {
        const url = new URL(created.confirmationUrl, window.location.origin);
        const providerPaymentId = url.searchParams.get('providerPaymentId');
        if (providerPaymentId && runtime.data?.fakePaymentsEnabled) {
          await api.post('/v1/billing/fake/complete', { providerPaymentId, scenario: 'succeeded' });
          navigate(`/payment/return?orderId=${encodeURIComponent(created.orderId)}`);
          return;
        }
        window.location.assign(created.confirmationUrl);
        return;
      }
      if (created.confirmationUrl) {
        await trackFunnel('payment_redirect_opened', { source: 'paywall', ...fields });
        setPaying(true);
        if (insideVerifiedMiniApp) openExternalUrl(created.confirmationUrl);
        else window.location.assign(created.confirmationUrl);
        return;
      }
      navigate(`/payment/return?orderId=${encodeURIComponent(created.orderId)}`);
    } catch (error) {
      setPaying(false);
      trackCheckoutError(productId, error);
      if (error instanceof ApiError && (error.status === 401 || error.code === 'NEED_STABLE_IDENTITY')) {
        onSelect(productId);
        return;
      }
      setCheckoutError(error instanceof Error && !isInternalClientError(error) ? error : new Error(CHECKOUT_RETRY_MESSAGE));
    }
  };
  useEffect(() => {
    if (selected && !receiptStep) void trackFunnel('package_cta_visible', { source: 'paywall', ...attribution, productId: selected.productId });
  }, [Boolean(selected), receiptStep]);
  const ctaPrice = selected?.price?.formatted ?? QUESTION_PACK_COPY[selectedId as keyof typeof QUESTION_PACK_COPY]?.priceLabel;
  return createPortal(<div className="modal-backdrop" role="presentation" onMouseDown={event => event.target === event.currentTarget && dismiss()}>
    <motion.div className="bottom-sheet paywall-sheet" role="dialog" aria-modal="true" aria-labelledby="paywall-title" initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }}>
      <button className="icon-button sheet-close" onClick={dismiss} aria-label="Закрыть"><X /></button>
      {receiptStep ? <><ReceiptContactStep email={email} setEmail={setEmail} error={emailError}
        onContinue={() => void startCheckout(true)} onBack={() => setReceiptStep(false)} busy={paying} /><ErrorNotice error={checkoutError} /></> : <>
      <div className="chooser-scroll">
        <h2 id="paywall-title">{preservedQuestion ? 'Получить ответ' : CHOOSER_TITLE}</h2>
        <p className="paywall-subtitle">{chooserSubtitle(Boolean(preservedQuestion))}</p>
        {preservedQuestion ? <div className="paywall-question" data-testid="preserved-question">
          <p className="paywall-kicker">Твой вопрос</p>
          {preservedQuestion.length >= 240 ? <p className="question-excerpt">{preservedQuestion.slice(0, 240)}...</p> : null}<details className="question-preview" open={preservedQuestion.length < 240}><summary>Показать вопрос полностью</summary><p>{preservedQuestion}</p></details>
        </div> : null}
        <div className="offer-list" role="listbox" aria-label="Пакеты вопросов">
          {offers.map(offer => {
            const copy = QUESTION_PACK_COPY[offer.productId as keyof typeof QUESTION_PACK_COPY];
            const pressed = offer.productId === selected?.productId;
            return <button key={offer.productId} type="button" role="option" aria-selected={pressed} className={pressed ? 'chooser-card selected' : 'chooser-card'} onClick={() => choosePack(offer.productId)}>
              <span>
                <strong>{copy?.title ?? offer.title}</strong>
                <small>{questionPackDescription(offer.productId, Boolean(preservedQuestion))}</small>
                {copy?.detail ? <small>{copy.detail}</small> : null}
              </span>
              <span className="chooser-price">{offer.price?.formatted}</span>
            </button>;
          })}
        </div>
      </div>
      <ErrorNotice error={checkoutError} />
      <button type="button" className="primary-button large paywall-cta" data-testid="purchase-cta" disabled={!selected || paying} onClick={() => void startCheckout()}>
        {paying ? 'Открываем оплату...' : selected ? `${preservedQuestion ? 'Получить ответ' : 'Продолжить'} за ${ctaPrice}` : purchaseCtaLabel(DEFAULT_QUESTION_PACK_ID, Boolean(preservedQuestion))}
      </button></> }
    </motion.div>
  </div>, document.body);
}

function PreservedCheckoutQuestion() {
  const pending = readPendingContinuation();
  if (!pending) return null;
  return <p className="paywall-preserved" data-testid="preserved-question">{pending.question}</p>;
}

export function ProductOffers({ compact = false, exhausted = false }: { compact?: boolean; exhausted?: boolean }) {
  const navigate = useNavigate();
  const catalog = useQuery({ queryKey: ['catalog'], staleTime: 300_000, queryFn: () => api.get<{ products: Product[] }>('/v1/catalog') });
  const offers = catalog.data?.products.filter(item => QUESTION_PACK_IDS.includes(item.productId) && item.checkoutEnabled && item.price) ?? [];
  const primary = offers.find(item => item.productId === PRIMARY_QUESTION_PACK_ID) ?? offers[0];
  const secondary = offers.filter(item => item !== primary);
  useEffect(() => {
    if (offers.length) {
      void trackFunnel('offers_impression', { source: 'offers' });
    }
  }, [offers.length]);
  if (!primary) return null;
  const openCheckout = (offer: Product) => {
    void trackFunnel('offer_selected', { productId: offer.productId, source: 'offers' });
    void trackFunnel('package_selected', { productId: offer.productId, source: 'offers' });
    navigate(`/checkout?productId=${encodeURIComponent(offer.productId)}`);
  };
  const offerIcon = (productId: string) => {
    if (productId === 'CHAT_15') return <Sparkle weight="fill" />;
    if (productId === 'CHAT_UNLIMITED_1M') return <InfinityIcon weight="duotone" />;
    return <ChatCircleDots weight="duotone" />;
  };
  return <section className={(compact ? 'offers compact' : 'offers') + (exhausted ? ' exhausted' : '')}>
    <div className="section-heading"><div>
      <p className="eyebrow">{exhausted ? 'Продолжить разбор' : 'Если захочется продолжить'}</p>
      <h2>{exhausted ? 'Продолжи разговор с нумерологом' : 'Больше пространства для вопросов'}</h2>
    </div></div>
    <button className="offer-primary" onClick={() => openCheckout(primary)}>
      <span className="offer-primary-head">
        <span className="offer-symbol" aria-hidden="true">{offerIcon(primary.productId)}</span>
        <span className="offer-copy"><strong>{primary.title}</strong><small>{primary.shortDescription ?? 'Без автопродления'}</small></span>
      </span>
      <span className="offer-primary-cta">Продолжить за {primary.price?.formatted}<ArrowRight weight="bold" /></span>
      <small className="offer-primary-note">Разовая оплата, без подписки. Ответы опираются на твою дату рождения и уже пройденный разбор.</small>
    </button>
    {secondary.length > 0 && <div className="offer-list offer-list-secondary">{secondary.map(offer => <button key={offer.productId} onClick={() => openCheckout(offer)} className="offer-row">
      <span className="offer-symbol" aria-hidden="true">{offerIcon(offer.productId)}</span>
      <span className="offer-copy"><strong>{offer.title}</strong><small>{offer.badge ? `${offer.badge} · ` : ''}{offer.shortDescription ?? 'Без автопродления'}</small></span>
      <span className="offer-price">{offer.price?.formatted}<CaretRight /></span>
    </button>)}</div>}
  </section>;
}

function reportViewPath(reportId: string, status?: string | null): string {
  if (status === 'generating' || status === 'ready_to_generate' || status === 'purchased') {
    return `/reports/generating/${encodeURIComponent(reportId)}`;
  }
  return `/reports/view/${encodeURIComponent(reportId)}`;
}

export function CheckoutPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params] = useSearchParams();
  const productId = params.get('productId') ?? '';
  const prepareIdParam = params.get('prepareId');
  const contextChoiceParam = params.get('contextChoice');
  const targetYearParam = params.get('targetYear');
  const questionRankParam = params.get('questionRank');
  const checkoutQuestionRank = questionRankParam === 'primary' || questionRankParam === 'secondary' ? questionRankParam : undefined;
  const [email, setEmail] = useState(rememberedReceiptEmail);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [receiptStep, setReceiptStep] = useState(false);
  const [successBalance, setSuccessBalance] = useState<EntitlementBalance | null>(null);
  const [resolvedPrepareId, setResolvedPrepareId] = useState<string | null>(prepareIdParam);
  const catalog = useQuery({
    queryKey: ['catalog'],
    staleTime: 300_000,
    queryFn: () => api.get<{ products: Product[] }>('/v1/catalog'),
  });
  const auth = useQuery({ queryKey: ['auth-me'], queryFn: () => api.get<{ hasStableIdentity: boolean; user: { displayName: string } | null }>('/v1/auth/me') });
  const receipt = useQuery({ queryKey: ['receipt-config'], queryFn: () => api.get<{ requireCustomerEmail: boolean }>('/v1/billing/receipt-config') });
  const runtime = useQuery({
    queryKey: ['runtime'],
    queryFn: () => api.get<{
      fakeAuthEnabled: boolean;
      fakePaymentsEnabled: boolean;
      paymentProvider: string;
      checkoutAvailable?: boolean;
    }>('/v1/runtime'),
  });
  const product = catalog.data?.products.find(item => item.productId === productId);
  const isReport = Boolean(product && (product.type === 'report' || isReportProductId(productId)));
  const telegramAuth = useTelegramAuthStatus();
  const telegramBootstrapPending = looksLikeTelegramMiniAppShell() && isTelegramAuthPending(telegramAuth);
  const insideVerifiedMiniApp = looksLikeTelegramMiniAppShell()
    || isTelegramMiniApp()
    || telegramAuth === 'authenticated'
    || telegramAuth === 'authenticating';
  const paymentsAvailable = Boolean(
    runtime.data?.checkoutAvailable
    ?? (runtime.data?.fakePaymentsEnabled || runtime.data?.paymentProvider === 'yookassa'),
  );
  const [openingPayment, setOpeningPayment] = useState(false);
  const emailRequired = Boolean(receipt.data?.requireCustomerEmail);
  useEffect(() => {
    if (product && !telegramBootstrapPending && auth.data?.user) {
      void trackFunnel('checkout_viewed', {
        productId, source: 'checkout',
        ...(params.get('sourceSurface') ? { sourceSurface: params.get('sourceSurface') as 'introductory_reading' | 'daily_forecast' | 'free_question' | 'chat' } : {}),
        ...(params.get('angleType') ? { angleType: params.get('angleType') ?? undefined } : {}),
      });
      if (productId === 'COMPATIBILITY_V1') void trackFunnel('compatibility_checkout_opened', { productId, source: 'checkout' });
    }
  }, [product?.productId, productId, telegramBootstrapPending, Boolean(auth.data?.user)]);

  useEffect(() => {
    if (productId === 'COMPATIBILITY_V1' && !prepareIdParam && !params.get('handoff'))
      navigate('/reports/COMPATIBILITY_V1', { replace: true });
  }, [productId, prepareIdParam]);

  const handoffParam = params.get('handoff');
  const [handoffState, setHandoffState] = useState<'idle' | 'exchanging' | 'done' | 'error'>('idle');
  const [handoffError, setHandoffError] = useState<string | null>(null);

  useEffect(() => {
    if (!handoffParam || handoffState !== 'idle') return;
    setHandoffState('exchanging');
    api.post<{
      userId: string;
      productId: string;
      reportContext: unknown;
      user: { userId: string; displayName: string };
    }>('/v1/checkout/handoff/exchange', {
      handoffToken: handoffParam,
      ...(productId ? { productId } : {}),
    })
      .then(() => {
        setHandoffState('done');
        void queryClient.invalidateQueries({ queryKey: ['auth-me'] });
        void queryClient.invalidateQueries({ queryKey: ['profile'] });
        const nextParams = new URLSearchParams(params);
        nextParams.delete('handoff');
        navigate(`/checkout?${nextParams.toString()}`, { replace: true });
      })
      .catch(err => {
        setHandoffState('error');
        setHandoffError(err instanceof Error ? err.message : 'Ссылка перехода устарела или уже была использована.');
      });
  }, [handoffParam, handoffState, productId, queryClient, navigate, params]);

  const checkoutReturnPath = () => {
    const next = new URLSearchParams({ productId });
    if (resolvedPrepareId ?? prepareIdParam) next.set('prepareId', resolvedPrepareId ?? prepareIdParam!);
    if (contextChoiceParam) next.set('contextChoice', contextChoiceParam);
    if (targetYearParam) next.set('targetYear', targetYearParam);
    return `/checkout?${next.toString()}`;
  };

  useEffect(() => {
    setResolvedPrepareId(prepareIdParam);
  }, [prepareIdParam]);

  const login = useMutation({
    mutationFn: async () => {
      const returnPath = checkoutReturnPath();
      if (runtime.data?.fakeAuthEnabled) {
        return api.post<{ outcome: string; pendingProductId: string | null }>('/v1/auth/telegram/fake-login', {
          subject: `web_${crypto.randomUUID().slice(0, 12)}`,
          firstName: auth.data?.user?.displayName ?? 'Пользователь',
          productId,
          returnPath,
        });
      }
      const started = await api.post<{ authorizationUrl: string }>('/v1/auth/telegram/start', {
        productId,
        returnPath,
      });
      window.location.assign(started.authorizationUrl);
      return started;
    },
    onSuccess: () => { void queryClient.invalidateQueries({ queryKey: ['auth-me'] }); },
  });

  const ensurePrepare = async (): Promise<string | null> => {
    if (!isReport) return null;
    if (resolvedPrepareId ?? prepareIdParam) return resolvedPrepareId ?? prepareIdParam;
    const prepared = await api.post<{
      alreadyOwned: boolean;
      reportId: string | null;
      prepareId: string | null;
    }>('/v1/reports/prepare', {
      productId,
      ...(contextChoiceParam ? { contextChoice: contextChoiceParam } : {}),
      ...(targetYearParam ? { targetYear: Number(targetYearParam) } : {}),
    });
    if (prepared.alreadyOwned && prepared.reportId) {
      navigate(reportViewPath(prepared.reportId), { replace: true });
      return null;
    }
    if (prepared.prepareId) setResolvedPrepareId(prepared.prepareId);
    return prepared.prepareId;
  };

  const checkout = useMutation({
    onError: error => { setOpeningPayment(false); trackCheckoutError(productId, error); },
    mutationFn: async () => {
      await trackFunnel('checkout_request_started', {
        productId, source: 'checkout',
        ...(params.get('sourceSurface') ? { sourceSurface: params.get('sourceSurface') as 'introductory_reading' | 'daily_forecast' | 'free_question' | 'chat' } : {}),
        ...(params.get('angleType') ? { angleType: params.get('angleType') ?? undefined } : {}),
        ...(checkoutQuestionRank ? { questionRank: checkoutQuestionRank } : {}),
      });
      await trackFunnel('payment_button_clicked', {
        productId, source: 'checkout',
        ...(params.get('sourceSurface') ? { sourceSurface: params.get('sourceSurface') as 'introductory_reading' | 'daily_forecast' | 'free_question' | 'chat' } : {}),
        ...(params.get('angleType') ? { angleType: params.get('angleType') ?? undefined } : {}),
        ...(checkoutQuestionRank ? { questionRank: checkoutQuestionRank } : {}),
      });
      const prepareId = await ensurePrepare();
      if (isReport && !prepareId) return null;
      const created = await api.post<{
        orderId: string;
        confirmationUrl: string | null;
        product: { title: string; formatted: string };
        reportId?: string | null;
      }>('/v1/billing/checkout', {
        productId,
        ...(prepareId ? { prepareId } : {}),
        ...(isReport && contextChoiceParam && !prepareId ? {
          reportContext: {
            contextChoice: contextChoiceParam,
            ...(targetYearParam ? { targetYear: Number(targetYearParam) } : {}),
          },
        } : {}),
        ...(targetYearParam && isReport ? { targetYear: Number(targetYearParam) } : {}),
        ...(emailRequired ? { receiptEmail: email.trim() } : {}),
        ...(params.get('sourceSurface') ? { sourceSurface: params.get('sourceSurface') } : {}),
        ...(params.get('angleType') ? { angleType: params.get('angleType') } : {}),
        ...(checkoutQuestionRank ? { questionRank: checkoutQuestionRank } : {}),
        ...(readLifecycleAttribution()?.sourceCampaign ? { sourceCampaign: readLifecycleAttribution()?.sourceCampaign } : {}),
      }, idempotencyKey('checkout'));
      if (emailRequired) rememberReceiptEmail(email.trim());
      if (created.confirmationUrl?.startsWith('/')) {
        const url = new URL(created.confirmationUrl, window.location.origin);
        const providerPaymentId = url.searchParams.get('providerPaymentId');
        if (providerPaymentId && runtime.data?.fakePaymentsEnabled) {
          const completed = await api.post<{
            orderId: string; status: string; balance: EntitlementBalance; reportId?: string | null;
          }>('/v1/billing/fake/complete', { providerPaymentId, scenario: 'succeeded' });
          if (isReport && completed.reportId) {
            navigate(reportViewPath(completed.reportId, 'generating'), { replace: true });
            return created;
          }
          if (isReport) {
            navigate(`/payment/return?orderId=${encodeURIComponent(created.orderId)}`, { replace: true });
            return created;
          }
          navigate(`/payment/return?orderId=${encodeURIComponent(created.orderId)}`);
          return created;
        }
        window.location.assign(created.confirmationUrl);
        return created;
      }
      if (created.confirmationUrl) {
        await trackFunnel('payment_redirect_started', { productId, source: 'checkout' });
        await trackFunnel('payment_redirect_opened', { productId, source: 'checkout' });
        setOpeningPayment(true);
        // Mini App: open YooKassa hosted page directly (never our /checkout handoff site).
        if (insideVerifiedMiniApp) {
          openExternalUrl(created.confirmationUrl);
        } else {
          window.location.assign(created.confirmationUrl);
        }
        return created;
      }
      navigate(`/payment/return?orderId=${encodeURIComponent(created.orderId)}`);
      return created;
    },
  });

  const startPayment = async (resumed = false) => {
    if (checkout.isPending || openingPayment) return;
    if (!resumed) void trackFunnel('purchase_cta_clicked', { productId, source: 'checkout' });
    const config = receipt.isSuccess ? receipt.data : (await receipt.refetch()).data;
    if (!config) { setEmailError('Не удалось проверить данные для чека. Попробуй ещё раз.'); setReceiptStep(true); return; }
    if (config.requireCustomerEmail && !RECEIPT_EMAIL_PATTERN.test(email.trim())) {
      setReceiptStep(true);
      setEmailError(resumed ? 'Проверь email и попробуй ещё раз.' : null);
      void trackFunnel(resumed ? 'receipt_contact_invalid' : 'receipt_contact_required', { productId, source: 'checkout' });
      return;
    }
    if (config.requireCustomerEmail) rememberReceiptEmail(email.trim());
    if (resumed) {
      void trackFunnel('receipt_contact_submitted', { productId, source: 'checkout' });
      void trackFunnel('checkout_resumed_after_receipt_contact', { productId, source: 'checkout' });
    }
    setEmailError(null);
    checkout.mutate();
  };

  // Kept for optional browser continuity / return flows; not used in normal Mini App Buy path.
  const miniAppHandoff = useMutation({
    mutationFn: async () => {
      const prepareId = await ensurePrepare();
      const reportContext = (contextChoiceParam || targetYearParam) ? {
        ...(contextChoiceParam ? { contextChoice: contextChoiceParam } : {}),
        ...(targetYearParam ? { targetYear: Number(targetYearParam) } : {}),
      } : undefined;
      const created = await api.post<{ handoffToken: string; checkoutUrl: string }>('/v1/checkout/handoff', {
        productId,
        prepareId: prepareId ?? null,
        reportContext,
      });
      openExternalUrl(created.checkoutUrl);
      return created;
    },
  });

  const purchaseIntent = useQuery({ queryKey: ['purchase-intent'], queryFn: () => api.get<{intent: {question?: string; sourceSurface?: 'chat'; productId?: string} | null}>('/v1/billing/purchase-intent'), enabled: !isReport });
  if (!product) return <div className="center-stage"><p>Продукт не найден.</p><button className="secondary-button" onClick={() => navigate('/numerologist')}>Назад</button></div>;

  if (telegramBootstrapPending) {
    return <div className="checkout-page">
      <button className="back-link" onClick={() => navigate(-1)}>Назад</button>
      <section className="checkout-card">
        <p className="eyebrow">Оплата разбора</p>
        <h1>{product.title}</h1>
        <p className="purchase-caption">Подтверждаем Telegram-аккаунт...</p>
        <button className="primary-button" disabled>Подтверждаем Telegram...</button>
      </section>
    </div>;
  }

  if (!isReport && !readPendingContinuation() && purchaseIntent.isPending) return <div className="center-stage" role="status">Восстанавливаем вопрос...</div>;
  if (!isReport && !params.get('direct')) return <PaywallSheet
    onClose={() => navigate('/numerologist')} onSelect={id => navigate(`/checkout?productId=${id}&direct=1`)}
    preservedQuestion={readPendingContinuation()?.question ?? purchaseIntent.data?.intent?.question} sourceSurface={readPendingContinuation()?.sourceSurface ?? purchaseIntent.data?.intent?.sourceSurface}
    angleType={readPendingContinuation()?.angleType} questionRank={readPendingContinuation()?.questionRank}
    retryProductId={params.get('retry') ? productId : undefined} />;

  if (receiptStep) return <div className="checkout-page"><section className="checkout-card">
    <ReceiptContactStep email={email} setEmail={setEmail} error={emailError} onContinue={() => void startPayment(true)}
      onBack={() => setReceiptStep(false)} busy={checkout.isPending || openingPayment} />
    <ErrorNotice error={checkout.error} variant="banner" />
  </section></div>;

  if (insideVerifiedMiniApp) {
    const busy = checkout.isPending || openingPayment || miniAppHandoff.isPending;
    return <div className="checkout-page" data-checkout-flow="direct-yookassa">
      <button className="back-link" onClick={() => navigate(-1)}>Назад</button>
      <section className="checkout-card">
        <p className="eyebrow">Оплата разбора</p>
        <h1>{product.title}</h1>
        <p>{product.shortDescription ?? (isReport ? 'Большой персональный разбор' : 'Доступ к вопросам нумерологу')}</p>
        {!isReport && <PreservedCheckoutQuestion />}
        <strong className="checkout-price">{product.price?.formatted}</strong>
        <p className="purchase-caption">Оплата откроется на защищённой странице YooKassa. Аккаунт уже сохранён.</p>
        <div className="account-chip">
          {auth.data?.hasStableIdentity
            ? `Аккаунт: ${auth.data.user?.displayName ?? 'Telegram'}`
            : 'Подтверждаем Telegram-аккаунт...'}
        </div>
        <ErrorNotice error={checkout.error ?? miniAppHandoff.error} variant="banner" />
        {paymentsAvailable ? (
          <button
            className="primary-button"
            disabled={busy || !auth.data?.hasStableIdentity}
            onClick={() => void startPayment()}
            data-payment-cta="direct"
          >
            {!auth.data?.hasStableIdentity
              ? 'Подтверждаем аккаунт...'
              : busy ? 'Открываем оплату…' : `Купить за ${product.price?.formatted}`} <ArrowRight />
          </button>
        ) : (
          <>
            <button className="primary-button" disabled title="Оплата временно недоступна" data-payment-cta="disabled">
              {`Купить за ${product.price?.formatted}`} <ArrowRight />
            </button>
            <p className="purchase-caption payment-notice">Оплата временно недоступна. Подключаем платёжный магазин.</p>
          </>
        )}
      </section>
    </div>;
  }

  if (successBalance) {
    const label = successBalance.unlimitedActive
      ? `Безлимит активен до ${formatUnlimitedUntil(successBalance.unlimitedUntil)}`
      : `У тебя теперь ${successBalance.paidQuestionsRemaining + successBalance.freeQuestionsRemaining} вопросов`;
    return <div className="center-stage checkout-page success-state">
      <motion.div initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
        <div className="sheet-symbol"><Check weight="bold" /></div>
        <h1>Готово. {label}</h1>
        <button className="primary-button" onClick={() => navigate('/numerologist', { state: continuationNavigationState(readPendingContinuation()) })}>Продолжить разговор</button>
      </motion.div>
    </div>;
  }

  const needsLogin = auth.data && !auth.data.hasStableIdentity;
  const handoffRecognized = handoffState === 'done' || Boolean(handoffParam && handoffState === 'exchanging');
  return <div className="checkout-page">
    <button className="back-link" onClick={() => navigate(-1)}>Назад</button>
    <section className="checkout-card">
      <p className="eyebrow">Подтверждение</p>
      <h1>{product.title}</h1>
      <p>{product.shortDescription ?? (isReport ? 'Большой персональный разбор' : 'Доступ к вопросам нумерологу')}</p>
      {!isReport && <PreservedCheckoutQuestion />}
      <strong className="checkout-price">{product.price?.formatted}</strong>
      <p className="purchase-caption">{isReport ? 'Разовая покупка. Разбор остаётся у тебя.' : 'Разовая оплата. Без автопродления.'}</p>
      <div className="account-chip">
        {handoffState === 'exchanging'
          ? 'Восстанавливаем аккаунт...'
          : auth.data?.hasStableIdentity
            ? `Аккаунт: ${auth.data.user?.displayName ?? 'Telegram'}`
            : handoffRecognized
              ? 'Восстанавливаем аккаунт...'
              : 'Аккаунт пока анонимный - сохраним через Telegram'}
      </div>
      <ErrorNotice error={login.error ?? checkout.error ?? (handoffError ? new Error(handoffError) : null)} variant="banner" />
      {handoffState === 'exchanging' ? (
        <button className="primary-button" disabled>Восстанавливаем аккаунт...</button>
      ) : needsLogin ? (
        <button className="primary-button telegram-login" disabled={login.isPending} onClick={() => login.mutate()}>
          {login.isPending ? 'Открываем Telegram...' : 'Войти через Telegram и оплатить'}
        </button>
      ) : paymentsAvailable ? (
        <button
          className="primary-button"
          disabled={checkout.isPending || openingPayment}
          onClick={() => void startPayment()}
          data-payment-cta="direct"
        >
          {(checkout.isPending || openingPayment) ? 'Открываем оплату…' : `Оплатить ${product.price?.formatted}`} <ArrowRight />
        </button>
      ) : (
        <>
          <button className="primary-button" disabled={true} title="Оплата временно недоступна" data-payment-cta="disabled">
            {`Оплатить ${product.price?.formatted}`} <ArrowRight />
          </button>
          <p className="purchase-caption payment-notice">Оплата временно недоступна (подключаем платёжный магазин).</p>
        </>
      )}
      <a href="https://t.me/numero_ai1_bot" className="secondary-button return-miniapp-button" target="_blank" rel="noopener noreferrer">
        Вернуться в Numerology AI
      </a>
    </section>
  </div>;
}

export function PaymentReturnPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params] = useSearchParams();
  const orderId = params.get('orderId') ?? '';
  const [tries, setTries] = useState(0);
  const [reportRedirected, setReportRedirected] = useState(false);
  const telegramAuth = useTelegramAuthStatus();
  const miniAppShell = looksLikeTelegramMiniAppShell() || isTelegramMiniApp();
  const waitingTelegram = miniAppShell && isTelegramAuthPending(telegramAuth);
  useEffect(() => { if (orderId) void trackFunnel('payment_returned_to_app', { source: 'checkout' }); }, [orderId]);
  const status = useQuery({
    queryKey: ['order', orderId, tries],
    enabled: Boolean(orderId) && !waitingTelegram,
    queryFn: () => api.get<{
      status: string;
      balance: EntitlementBalance;
      productId: string;
      reportId?: string | null;
      pendingQuestion?: { question: string; sourceSurface?: 'chat'; angleType?: string; questionRank?: 'primary' | 'secondary' } | null;
    }>(`/v1/billing/orders/${orderId}`),
    retry: (count, error) => {
      if (error instanceof ApiError && (error.status === 401 || error.status === 409)) return false;
      return count < 1;
    },
    refetchInterval: query => {
      const value = query.state.data?.status;
      return tries < 20 && (value === 'pending' || value === 'created') ? 1500 : false;
    },
  });

  useEffect(() => {
    if (status.data?.status === 'pending' && tries < 20) {
      const timer = window.setTimeout(() => setTries(value => value + 1), 1500);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [status.data?.status, tries]);

  useEffect(() => {
    if (status.data?.status === 'succeeded') {
      if (status.data.pendingQuestion?.question) rememberPendingContinuation(status.data.pendingQuestion);
      void queryClient.invalidateQueries({ queryKey: ['entitlements'] });
      void queryClient.invalidateQueries({ queryKey: ['my-reports'] });
    }
  }, [status.data?.status, queryClient]);

  useEffect(() => {
    if (reportRedirected || status.data?.status !== 'succeeded') return;
    const reportId = status.data.reportId;
    const productId = status.data.productId;
    if (reportId || isReportProductId(productId)) {
      setReportRedirected(true);
      const timer = window.setTimeout(() => {
        if (reportId) navigate(reportViewPath(reportId, 'generating'), { replace: true });
        else navigate('/reports?tab=mine', { replace: true });
      }, 1600);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [status.data, reportRedirected, navigate]);

  if (!orderId) return <div className="center-stage"><p>Нет номера заказа.</p></div>;
  if (waitingTelegram) {
    return <div className="center-stage"><div className="loading-card" role="status"><span className="mini-orbit" /><span>Подтверждаем Telegram...</span></div></div>;
  }
  if (status.error instanceof ApiError && (status.error.status === 401 || status.error.code === 'ONBOARDING_INCOMPLETE' || status.error.status === 409)) {
    return <div className="center-stage checkout-page success-state">
      <div className="sheet-symbol"><Check weight="bold" /></div>
      <h1>Нужно проверить оплату</h1>
      <p className="purchase-caption">Открой приложение через Telegram, чтобы восстановить аккаунт и проверить статус оплаты.</p>
      <a className="primary-button" href="https://t.me/numero_ai1_bot">Открыть Numerology AI</a>
    </div>;
  }
  if (status.data?.status === 'pending' || status.data?.status === 'created') return <div className="center-stage checkout-page"><h1>Оплата ожидает подтверждения</h1><p>Если оплата уже прошла, проверь статус. Вопрос сохранён.</p><button className="primary-button" onClick={() => void status.refetch()}>Проверить оплату</button><button className="secondary-button" onClick={() => navigate('/numerologist')}>Вернуться к разговору</button></div>;
  if (status.isPending) {
    return <div className="center-stage"><div className="loading-card" role="status"><span className="mini-orbit" /><span>Проверяем оплату...</span></div></div>;
  }
  if (status.data?.status === 'succeeded') {
    const reportId = status.data.reportId;
    const productId = status.data.productId;
    if (reportId || isReportProductId(productId)) {
      return <div className="center-stage checkout-page success-state">
        <div className="sheet-symbol"><Check weight="bold" /></div>
        <h1>Покупка готова. Теперь собираю твой большой разбор.</h1>
        <p className="purchase-caption">Открою его, как только текст будет готов.</p>
        {reportId ? (
          <button className="primary-button" onClick={() => navigate(reportViewPath(reportId, 'generating'))}>Смотреть статус</button>
        ) : (
          <button className="primary-button" onClick={() => navigate('/reports?tab=mine')}>К моим разборам</button>
        )}
      </div>;
    }
    const balance = status.data.balance;
    const label = balance.unlimitedActive
      ? `Безлимит активен до ${formatUnlimitedUntil(balance.unlimitedUntil)}`
      : `У тебя теперь ${balance.paidQuestionsRemaining} вопросов`;
    return <div className="center-stage checkout-page success-state">
      <div className="sheet-symbol"><Check weight="bold" /></div>
      <h1>Готово. {label}</h1>
      <button className="primary-button" onClick={() => navigate('/numerologist', { state: continuationNavigationState(status.data?.pendingQuestion ?? null) })}>Продолжить разговор</button>
    </div>;
  }
  if (status.data?.status === 'canceled') {
    return <div className="center-stage checkout-page"><h1>Оплата не завершилась</h1><p>Выбранный вариант сохранён. Можно попробовать ещё раз.</p><button className="primary-button" onClick={() => navigate(`/checkout?productId=${encodeURIComponent(status.data?.productId ?? 'CHAT_5')}&retry=1`)}>Вернуться к оплате</button></div>;
  }
  return <div className="center-stage checkout-page"><h1>Не удалось подтвердить оплату</h1><ErrorNotice error={status.error} variant="banner" /><button className="primary-button" onClick={() => navigate(`/checkout?productId=${encodeURIComponent(status.data?.productId ?? 'CHAT_15')}`)}>Повторить оплату</button></div>;
}

export function PurchasesSection() {
  const history = useQuery({
    queryKey: ['purchases'],
    staleTime: 60_000,
    queryFn: () => api.get<{ purchases: { title: string; amountMinor: number; currency: string; status: string; createdAt: string }[] }>('/v1/billing/history'),
  });
  const items = history.data?.purchases ?? [];
  return <section className="purchases-section">
    <div className="section-heading"><div><p className="eyebrow">Аккаунт</p><h2>Покупки</h2></div></div>
    {history.isPending && <p className="purchase-caption">Загружаем историю...</p>}
    {!history.isPending && !items.length && <p className="purchase-caption">Пока нет покупок.</p>}
    {items.length > 0 && (
      <div className="purchase-list">
        {items.map(item => (
          <div key={item.createdAt + item.title} className="purchase-row">
            <span className="purchase-meta"><strong>{item.title}</strong><small>{new Intl.DateTimeFormat('ru-RU').format(new Date(item.createdAt))} · {item.status}</small></span>
            <strong className="purchase-amount">{new Intl.NumberFormat('ru-RU').format(item.amountMinor / 100)} ₽</strong>
          </div>
        ))}
      </div>
    )}
  </section>;
}

export function AuthConflictPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';
  const resolve = useMutation({
    mutationFn: (action: 'keep_telegram_account' | 'transfer_anonymous_progress') =>
      api.post('/v1/auth/telegram/resolve-conflict', { conflictToken: token, action }),
    onSuccess: () => navigate('/numerologist', { replace: true }),
  });
  return <div className="center-stage checkout-page">
    <h1>Этот Telegram уже связан с другим аккаунтом</h1>
    <p>Мы не смешиваем данные молча. Можно продолжить с сохранённым Telegram-аккаунтом.</p>
    <ErrorNotice error={resolve.error} variant="banner" />
    <button className="primary-button" disabled={resolve.isPending} onClick={() => resolve.mutate('keep_telegram_account')}>Продолжить с Telegram-аккаунтом</button>
  </div>;
}
